import { format } from 'date-fns';
import KNB from '@wmfe/mach-pro-knb';
import { safeParseJSONStr } from '@/src/utils';
import { i18nClient } from '@sailor/i18n-mach-pro/lib';
import { DROP_OFF_SPOT_TYPE, DROP_OFF_TYPE } from '@/src/components/DropOffView/constant';
import { validateCoordinatesEnable } from '@/src/utils/coordinate';
import { reportCodeTryException } from '@/src/common/exception';
import { ORDER_STATUS_CODE } from '../interface/constant';

/**
 * 确定订单是否为待支付
 * @param orderStatus 订单状态
 * @returns
 */
export function isOrderWaitPay(orderStatus) {
  return orderStatus === ORDER_STATUS_CODE.CREATED || orderStatus === ORDER_STATUS_CODE.PICKUP_WAIT_PAY;
}
/**
 * 确定订单是否为待接单
 * @param orderStatus 订单状态
 * @returns
 */
export function isWaitShopTakeOrder(orderStatus) {
  return orderStatus === ORDER_STATUS_CODE.PAYED || orderStatus === ORDER_STATUS_CODE.PICKUP_WAIT_TAKE_ORDER;
}
/**
 * 确定订单是否为完成状态
 * @param orderStatus 订单状态
 * @returns
 */
export function isFinishOrder(orderStatus) {
  return orderStatus === ORDER_STATUS_CODE.FINISHED || orderStatus === ORDER_STATUS_CODE.PICKUP_ORDER_COMPLETE;
}
/**
 * 确定订单是否为取消状态
 * @param orderStatus 订单状态
 * @returns
 */
export function isCancelOrder(orderStatus) {
  return orderStatus === ORDER_STATUS_CODE.CANCELED;
}

/**
 * 带骑手取餐
 * @param orderStatus
 * @returns
 */
export function isWaitRiderTake(orderStatus) {
  return orderStatus === ORDER_STATUS_CODE.GRAB || orderStatus === ORDER_STATUS_CODE.ARRIVAL;
}

/**
 * 订单状态描述
 * @param statusInfo
 * @param remainTime
 * @returns
 */
export function getDeliveryStatusDesc(statusInfo, remainTime: number) {
  if (!statusInfo) {
    return '';
  }
  let orderStatusDesc = statusInfo.orderStatusDesc || '';
  const orderStatus = statusInfo.orderStatus;
  const isWaitPay = isOrderWaitPay(orderStatus);

  if ((remainTime && remainTime > 0) || isWaitPay) {
    // 由于 JavaScript 的 Date 对象本身存储的是 UTC 时间，date-fns 默认就是按照 UTC 时间进行操作的
    const date = new Date(Math.floor(remainTime) * 1000);
    const formatRemainTime = format(date, 'mm:ss');

    if (orderStatusDesc?.includes('{}')) {
      orderStatusDesc = orderStatusDesc.replace('{}', formatRemainTime);
    } else if (isWaitPay) {
      // 兼容服务端没有返回{}的情况
      orderStatusDesc = `${orderStatusDesc}${formatRemainTime}`;
    }
  } else if (orderStatusDesc?.includes('{}')) {
    orderStatusDesc = orderStatusDesc.replace('{}', '00:00');
  }
  return orderStatusDesc;
}

/**
 * 获取用户位置信息
 * @param options
 * @returns
 */
export async function getUserLocation(options = {}): Promise<any> {
  return new Promise((resolve, reject) => {
    KNB.getLocation({
      type: 'WGS84',
      cache: false,
      timeout: 3000,
      ...options,
      success(data: any) {
        const { lat, lng } = data || {};
        const location = {
          latitude: lat,
          longitude: lng,
        };
        if (data && validateCoordinatesEnable(location)) {
          resolve({ code: 0, data: location });
        } else {
          // 获取定位信息失败
          reject({ code: 10001, data: {} });
        }
      },
      fail() {
        // 调用KNB桥失败
        reject({ code: 10002, data: {} });
      },
    });
  });
}

/**
  * 获取地址备注信息
  * @returns
  */
export function getAddressRemarkInfo(detailData) {
  if (!detailData) {
    return;
  }
  const { orderBaseInfo, addressInfo } = detailData;
  const addressExtra = safeParseJSONStr(addressInfo?.extra);
  const { deliveryType, deliveryLocationType } = addressExtra || {};
  // 未选择交付方式
  if (deliveryType === undefined || deliveryType === null) {
    return orderBaseInfo.orderRemark;
  } else {
    const reachOption = {
      1: i18nClient.getText('address_when_can_not_reach_option1_low'),
      2: i18nClient.getText('address_when_can_not_reach_option2_low'),
      3: i18nClient.getText('address_when_can_not_reach_option3_low'),
    };
    const addressList: any = [];
    const remark = addressExtra?.remark || '';
    // 联系不上放在哪里
    const address = reachOption[deliveryLocationType];
    if (deliveryType === DROP_OFF_TYPE.HAND_TO_ME) {
      const title = i18nClient.getText('drop_off_option1');
      addressList.push(`${title}`);
      if (remark) {
        addressList.push(remark);
      }
      if (address) {
        const subtitle = i18nClient.getText('drop_off_instruction_2', { address });
        addressList.push(`${subtitle}`);
      }
    } else if (deliveryType === DROP_OFF_TYPE.LEAVE_AT_DESIGNATED_SPOT) {
      // 只填写了【希望交付的地点】，且选择非“其他”
      if (deliveryLocationType === DROP_OFF_SPOT_TYPE.OTHER) {
        const title = i18nClient.getText('drop_off_option2');
        addressList.push(title);
        if (remark) {
          addressList.push(remark);
        }
      } else { // 只填写的了【希望交付的地点】，且选择了“其他”（此时文字输入信息必填）
        const address = reachOption[deliveryLocationType];
        if (address) {
          const title = i18nClient.getText('leave_at_title', { address });
          addressList.push(title);
        }
        if (remark) {
          addressList.push(remark);
        }
      }
    }
    const catStr = i18nClient.getText('i18n_period');
    return addressList.join(`${catStr}`);
  }
}

/**
 * 获取优惠类型用于 b_waimai_c_overseas_tr1j6t8d_mv 埋点的 discount_activity_type 0:满减 1:折扣 2:折扣菜 3:新客满减 4:新客折扣 9:新人折扣菜
 * @param activityTypeList 2:满减 3:满折 18:spu纬度折扣菜 9:使用红包 4:减配送费 5:一口价商品满减 21:免配送费
 * @param shopNewUser 1:门店新客 0:非门店新客
 */
export function getDiscountActivityType(activityTypeList: number[] = [], matchShopNewUser, orderInfo) {
  if (!Array.isArray(activityTypeList) || activityTypeList.length === 0) {
    return -999;
  }
  const isShopNewUser = matchShopNewUser === 1;
  let discountActivityTypes: number[] = [];
  // 满减
  const isReduce = activityTypeList.indexOf(2) !== -1;
  if (isReduce) {
    if (isShopNewUser) {
      discountActivityTypes.push(3);
    } else {
      discountActivityTypes.push(0);
    }
  }
  // 折扣
  const isDiscount = activityTypeList.indexOf(3) !== -1;
  if (isDiscount) {
    if (isShopNewUser) {
      discountActivityTypes.push(4);
    } else {
      discountActivityTypes.push(1);
    }
  }
  // 折扣菜
  const discountTypeList = getDiscountTypeList(orderInfo, activityTypeList);
  if (discountTypeList?.length > 0) {
    discountActivityTypes = discountActivityTypes.concat(discountTypeList);
  }

  // 919活动
  const isEvent919 = activityTypeList.indexOf(919) !== -1;
  if (isEvent919) {
    discountActivityTypes.push(6);
  }

  return discountActivityTypes.join(',') || -999;
}

// 获取折扣菜类型
function getDiscountTypeList(orderInfo, activityTypeList) {
  if (!activityTypeList?.some?.((type) => [18, 5].includes(type))) {
    return [];
  }
  return orderInfo?.skuList?.reduce((acc, sku) => {
    const targetUserType = sku?.targetUserType || 0;
    const tags = sku?.tags || [];
    if (targetUserType === 1 && (tags?.some?.((type) => [1, 3].includes(type))) && !acc.includes(9)) {
      acc.push(9);
    } else if (!acc.includes(2)) {
      acc.push(2);
    }
    return acc;
  }, []) || [];
}

/**
 *  将json字符串转换为json对象
 * @param text 需要转换的字符串
 */
export function jsonParse(text: string): any {
  if (!text) return null;
  let res: any = null;
  try {
    res = JSON.parse(text);
  } catch (error) {
    res = text;
  }
  return res;
}

const reg = /\r?\n/;
/**
 * 获取分割后的数组
 * @param {string} 原文本
 */
export function splitText(text: string) {
  if (!text) return [];
  return text.split(reg);
}

export function splitTextWithDot(text: string) {
  if (!text) return [];
  return text.split('.');
}

// 高亮属性
export interface HighlightData {
  text: string;
  highlight: boolean;
}

/**
 * 高亮属性文案处理
 */
export function highlightListForText(text: string): HighlightData[] {
  if (!text) {
    return [];
  }

  const highlights: HighlightData[] = [];
  const segments = text.split('<highlight>');

  for (const segment of segments) {
    if (segment.includes('</highlight>')) {
      const [highlighted, normal] = segment.split('</highlight>');
      if (highlighted) {
        highlights.push({ text: highlighted, highlight: true });
      }
      if (normal) {
        highlights.push({ text: normal, highlight: false });
      }
    } else {
      highlights.push({ text: segment, highlight: false });
    }
  }

  return highlights;
}

/**
 * 请求推送权限是否开启
 */
export function shouldDisplayPushTips() {
  const SLCommon = Mach.requireModule('SLCommon');
  if (SLCommon.isOpenPush()) {
    return false;
  }
  const value = SLCommon.getStorage({
    key: 'order.status.golden.push.tips'
  });
  if (value) {
    const timestamp = Mach.env.networkTimestamp();
    if (timestamp - parseFloat(value) < 24 * 60 * 60 * 7) {
      return false;
    }
  }
  return true;
}

/**
 * 存储关闭push提示的时间
 */
export function storeClosePushTips() {
  const SLCommon = Mach.requireModule('SLCommon');
  const timestamp: any = Mach.env.networkTimestamp();
  SLCommon.setStorage({
    key: 'order.status.golden.push.tips',
    value: `${timestamp}`
  });
}

/**
 * rgba转16进制
 * @param rgba
 * @returns
 */
export function rgbaToHex(r, g, b, a) {
  // 将每个颜色通道转换为两位十六进制
  const toHex = (n) => {
    const hex = n.toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };

  // 透明度转换为 0-255 范围内的整数
  const alpha = Math.round(a * 255);
  return `#${toHex(r)}${toHex(g)}${toHex(b)}${toHex(alpha)}`;
}

/**
 * 记录送达反馈记录
 * @param orderViewId
 * @returns
 */
export function storeDeliveryFeedbackStatus(orderViewId: string) {
  if (!orderViewId) {
    return;
  }
  const Storage = Mach.requireModule('Storage');
  Storage.setBool({
    channel: 'mach',
    key: orderViewId,
    value: true,
  });
}

/**
 * 读取送达反馈记录
 * @param orderViewId
 * @returns
 */
export function getDeliveryFeedbackStatus(orderViewId: string) {
  if (!orderViewId) {
    return false;
  }
  const Storage = Mach.requireModule('Storage');
  const result = Storage.getBool({
    channel: 'mach',
    key: orderViewId,
  });
  return result;
}

/**
 * 毫秒转分钟
 * @param val
 * @returns
 */
export function milli2minute(val: any): number {
  try {
    const numVal = Number(val);
    if (Number.isNaN(numVal)) {
      return 0;
    }
    return numVal / (1000 * 60);
  } catch (error) {
    reportCodeTryException({
      message: 'milli2minute',
      extra: error
    });
    return 0;
  }
}

/**
 * 存储弹窗展示标识
 */
export function storeDisplayPopupView(popupViewId: string) {
  if (!popupViewId) {
    return;
  }
  const Storage = Mach.requireModule('Storage');
  Storage.setBool({
    channel: 'mach',
    key: popupViewId,
    value: true,
  });
}

/**
 * 判断是否展示过弹窗
 * @param popupViewId
 * @returns
 */
export function hasDisplayPopupView(popupViewId: string) {
  if (!popupViewId) {
    return false;
  }
  const Storage = Mach.requireModule('Storage');
  const result = Storage.getBool({
    channel: 'mach',
    key: popupViewId,
  });
  return result;
}
